import "./CardClinicalData.scss";
import clsx from "clsx";
import { ArrowDown } from "@/icons";
import { Button, Row } from "@/components";
import { useEffect, useState } from "react";

type CardClinicalDataProps = {
  information?: { label: string; value: string }[];
  className?: string;
  clinicalCode?: string;
  isOpen?: boolean;
  hideArrow?: boolean;
  onToggle?: () => void;
  hasBorder?: boolean;
  clinicalLabel?: string;
  label?: string;
  classNameTitle?: string;
  deleteLabel?: string;
  onDelete?: () => void;
  deleteButtonPosition?: "left" | "right";
  statusIndex?: 0 | 1 | 2;
};

export const CardClinicalData = ({
  information = [],
  className = "",
  clinicalCode = "",
  isOpen,
  hideArrow = false,
  onToggle,
  hasBorder = true,
  clinicalLabel = "",
  label,
  classNameTitle = "fw-semibold fs-5 md-fs-4 text-color-darker",
  deleteLabel,
  onDelete,
  deleteButtonPosition = "right",
  statusIndex,
}: CardClinicalDataProps) => {
  const hasInformation = information.length > 0;
  const [internalOpen, setInternalOpen] = useState(false);

  const statusColors = [undefined, "#995C00", "#008055"]; // 0: default (grigio), 1: inserito/in corso (giallo), 2: risolta (verde)
  const labelBackgroundColor =
    statusIndex !== undefined ? statusColors[statusIndex] : undefined;

  useEffect(() => {
    if (isOpen !== undefined) {
      setInternalOpen(isOpen);
    }
  }, [isOpen]);

  const computedIsOpen = hideArrow ? true : (isOpen ?? internalOpen);
  const canToggle = hasInformation && !hideArrow;

  const handleToggle = () => {
    if (!hasInformation || hideArrow) return;
    onToggle?.();
    if (isOpen === undefined) {
      setInternalOpen((prev) => !prev);
    }
  };

  return (
    <div className={"rounded shadow w-full card-clinical-data-container"}>
      <button
        className={clsx(
          computedIsOpen && hasBorder && "ui-has-border-bottom",
          "ui-btn-accordion-clinical-data",
          className,
        )}
        type="button"
        aria-label={`${clinicalCode}${clinicalLabel ? ` - ${clinicalLabel}` : ""}${label ? ` - ${label}` : ""}${canToggle ? (computedIsOpen ? " - Comprimi dettagli" : " - Espandi dettagli") : ""}`}
        aria-expanded={
          hasInformation && !hideArrow ? computedIsOpen : undefined
        }
        onClick={canToggle ? handleToggle : undefined}
        disabled={!canToggle}
        style={{ cursor: hideArrow ? "default" : undefined }}
      >
        <div className="ui-header-content p-x24">
          <Row gap={"x24"} alignItems={"center"} className="first-row">
            <div className="clinical-data-info">
              <h1 className={clsx(classNameTitle)} title={clinicalCode}>
                {clinicalCode}
              </h1>
              {clinicalLabel && (
                <>
                  <span className="ui-divider"></span>
                  <p
                    className={"fs-7 md-fs-6 text-muted fw-semibold"}
                    title={clinicalLabel}
                  >
                    {clinicalLabel}
                  </p>
                </>
              )}
            </div>
            {hasInformation && !hideArrow && (
              <ArrowDown
                color="primary"
                className={clsx("ui-arrow-card-clinical-data mobile-arrow", {
                  "ui-expanded-card-clinical-data": computedIsOpen,
                })}
                aria-hidden="true"
              />
            )}
          </Row>

          <Row
            gap={"x16"}
            alignItems={"center"}
            className="ui-arrow-container desktop-row"
          >
            {label && statusIndex !== undefined && (
              <div
                className={
                  "fs-7 fw-bold bg-secondary text-white rounded py-x4 px-x16 desktop-label"
                }
                style={{
                  width: "max-content",
                  ...(labelBackgroundColor && {
                    backgroundColor: labelBackgroundColor,
                  }),
                }}
              >
                {label}
              </div>
            )}
            {hasInformation && !hideArrow && (
              <ArrowDown
                color="primary"
                className={clsx("ui-arrow-card-clinical-data desktop-arrow", {
                  "ui-expanded-card-clinical-data": computedIsOpen,
                })}
                aria-hidden="true"
              />
            )}
          </Row>

          {label && statusIndex !== undefined && (
            <div
              className={
                "fs-7 fw-bold bg-secondary text-white rounded py-x4 px-x16 mobile-label"
              }
              style={{
                width: "max-content",
                ...(labelBackgroundColor && {
                  backgroundColor: labelBackgroundColor,
                }),
              }}
            >
              {label}
            </div>
          )}
        </div>
      </button>

      {computedIsOpen && hasInformation && (
        <dl
          className={clsx(
            "p-x24",
            information.length > 1
              ? "ui-card-clinical-data"
              : "base-row py-x16",
          )}
        >
          {information.map(({ label, value }) => (
            <div key={label}>
              <dt
                className={clsx(
                  "fs-custom fw-bold text-slate-28",
                  information.length === 1 && "me-x8 mb-0",
                )}
              >
                {label}:
              </dt>
              <dd className="fs-custom fw-regular text-slate-28 mb-0">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {computedIsOpen && deleteLabel && !hideArrow && (
        <Row
          className={clsx(
            "align-items-center px-x24 w-full bg-lightblue-a03 py-x16",
            deleteButtonPosition === "right"
              ? "justify-content-end"
              : "justify-content-start",
          )}
        >
          <Button
            variant={"secondary"}
            size={"3"}
            variantBg={"bg-lightblue-a03"}
            onClick={onDelete}
          >
            {deleteLabel}
          </Button>
        </Row>
      )}
    </div>
  );
};
